import {
  useAppointmentProposalShare,
  useToast,
} from '@airship-platform/airship-patterns';
import { EmptyState } from '@airship-platform/jam-web/components';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppointments } from '../../../../data-access/appointments';
import SendSurveyForm, {
  SendSurveyFormInputs,
  SendSurveyFormSchema,
} from '../send-survey-section/SendSurveyForm';

const removeDuplicates = <T extends { value: string }>(
  v: T,
  i: number,
  arr: T[]
) =>
  arr.findIndex((e) => e.value.toLowerCase() === v.value.toLowerCase()) === i;

type SendSurveySectionProps = {
  defaultValues: SendSurveyFormInputs;
  formId?: string;
  onSubmit: (data: SendSurveyFormInputs) => Promise<void>;
};

const SendSurveySection: React.FC<SendSurveySectionProps> = ({
  defaultValues,
  formId,
  onSubmit,
}) => {
  const { showToast } = useToast();
  const { currentAppointment } = useAppointments();
  const { shareCustomerSurvey } = useAppointmentProposalShare();

  const sendSurveyForm = useForm<SendSurveyFormInputs>({
    resolver: zodResolver(SendSurveyFormSchema),
    defaultValues,
  });

  if (!currentAppointment) {
    return (
      <EmptyState
        title="No appointemnt"
        subtitle="Unable to fetch appointment"
      />
    );
  }

  const sendCustomerSurvey = async (formData: SendSurveyFormInputs) => {
    const contacts = formData.surveyContacts
      .concat({
        type: 'Email',
        value: formData.customContact.trim(),
      })
      .filter((d) => d.value)
      .filter(removeDuplicates);

    if (contacts.length) {
      await shareCustomerSurvey(contacts, currentAppointment.appointmentId);
      showToast('Customer survey shared!', 'success');
    }

    return contacts
      .concat(defaultValues.surveyContacts)
      .filter(removeDuplicates);
  };

  const onSendSurvey = async (formData: SendSurveyFormInputs) => {
    const contacts = await sendCustomerSurvey(formData);
    await onSubmit({ surveyContacts: contacts, customContact: '' });
  };

  return (
    <Box
      component={'form'}
      id={formId}
      onSubmit={sendSurveyForm.handleSubmit(onSendSurvey)}
      autoComplete="off"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
      }}
    >
      <FormProvider {...sendSurveyForm}>
        <SendSurveyForm
          contacts={(currentAppointment.customer.contacts || [])
            .concat(defaultValues.surveyContacts)
            .filter(removeDuplicates)}
        />
      </FormProvider>
    </Box>
  );
};

export default SendSurveySection;
